import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CampaignContext } from "screens/Campaigns/CreateCampaign/Context";
import CustomTextInput from "global/components/CustomTextInput/CustomTextInput";
import InboxPreview from "./InboxPreview";
import EmojiPicker from "emoji-picker-react";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import { cloneDeep } from "lodash";
import { isTruthy } from "helpers/methods";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import SuggestionBox from "screens/Shared/SuggestionBox";
import { useAppSelector } from "utils/hooks";
import { selectIsFirstNameMandatory } from "redux/generalSlice";

const SubjectSectionTestingVariable = () => {
  const [openEmojiPicker, setOpenEmojiPicker] =
    useState<HTMLButtonElement | null>(null);
  const {
    submitCampaign,
    setSubmitCampaign,
    managingStep,
    setManagingStep,
    saveAsDraft,
  } = useContext(CampaignContext);
  const [formFields, setFormFields] = useState({
    subject: [] as { error: string; value: string }[],
  });
  const isFirstNameMandatory = useAppSelector(selectIsFirstNameMandatory);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (managingStep === "subject-section") {
      updateFormFields();
    }
  }, [managingStep]);

  const updateFormFields = () => {
    const subjects = Array.from({
      length: submitCampaign.abTestDetails?.testingVariants ?? 0,
    }).map((_, i) => {
      if (isTruthy(submitCampaign.abTestDetails?.testingValues[i])) {
        return {
          value: submitCampaign.abTestDetails?.testingValues[i] ?? "",
          error: "",
        };
      }
      return { value: "", error: "" };
    });
    setFormFields({
      ...formFields,
      subject: subjects,
    });
  };

  const handleSave = async () => {
    let error = false;
    const clonedFormFields = cloneDeep(formFields);
    clonedFormFields.subject.forEach((field) => {
      if (!isTruthy(field.value)) {
        error = true;
        field.error = "Please enter the subject";
      }
      if (isTruthy(field.value) && isFirstNameMandatory && !field.value.trim().includes("$FIRST_NAME")) {
        error = true;
        field.error = "Subject must include $FIRST_NAME";
      }
    });
    if (error) {
      return setFormFields(clonedFormFields);
    }
    setSaving(true);
    const campaign = {
      ...submitCampaign,
      abTestDetails: {
        ...submitCampaign.abTestDetails,
        testingValues: clonedFormFields.subject.map((field) => field.value),
      },
    };
    setSubmitCampaign(campaign);
    await saveAsDraft(campaign);
    setManagingStep("");
    setSaving(false);
  };

  const getExpandedView = () => {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={4} width="100%">
            <SvgIcon>
              <CheckCircleIcon
                htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
              />
            </SvgIcon>
            <Stack spacing={2} width="100%">
              <Stack spacing={0.5}>
                <Typography variant="h4">Subject</Typography>
                <Typography variant="body1">
                  Create a compelling subject to boost open rates
                </Typography>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack>
                    {formFields.subject.map((field, index: number) => (
                      <>
                        <CustomTextInput
                          label={`Subject Line (Version ${index + 1})`}
                          placeHolder="Enter your subject line"
                          value={field.value}
                          onChange={(event: any) => {
                            const clonedFormFields = cloneDeep(formFields);
                            clonedFormFields.subject[index] = {
                              error: "",
                              value: event.target.value,
                            };
                            setFormFields(clonedFormFields);
                          }}
                          required
                          error={field.error}
                          propsToInputElement={{ minLength: 1, maxLength: 90 }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <span style={{ fontSize: "0.75rem" }}>
                                  {field.value?.length} / 90
                                </span>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Stack direction="row" justifyContent="flex-end">
                          {/* <Tooltip title="Generate subject lines with AI">
                      <IconButton>
                        <SvgIcon>
                          <AutoAwesomeIcon htmlColor="#000" fontSize="small" />
                        </SvgIcon>
                      </IconButton>
                    </Tooltip> */}
                          <Tooltip title="Add emojis">
                            <IconButton
                              id={`${index}`}
                              onClick={(
                                event: React.MouseEvent<HTMLButtonElement>
                              ) => {
                                setOpenEmojiPicker(event.currentTarget);
                              }}
                            >
                              <SvgIcon>
                                <EmojiEmotionsIcon
                                  htmlColor="#7551D0"
                                  fontSize="small"
                                />
                              </SvgIcon>
                            </IconButton>
                          </Tooltip>
                          <Popover
                            open={Boolean(openEmojiPicker)}
                            anchorEl={openEmojiPicker}
                            onClose={() => setOpenEmojiPicker(null)}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                          >
                            <EmojiPicker
                              onEmojiClick={(emoji) => {
                                const index = parseInt(
                                  openEmojiPicker?.id ?? ""
                                );
                                const clonedFormFields = cloneDeep(formFields);
                                clonedFormFields.subject[index] = {
                                  error: "",
                                  value: `${formFields.subject[index].value}${emoji.emoji}`,
                                };
                                setFormFields(clonedFormFields);
                              }}
                            />
                          </Popover>
                        </Stack>
                      </>
                    ))}
                  </Stack>
                  {isFirstNameMandatory && (
                    <SuggestionBox title="$FIRST_NAME is required to enhance personalization." />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <InboxPreview
                    for={"subject"}
                    string1={formFields.subject[0]?.value ?? ""}
                    string2={formFields.subject[1]?.value ?? ""}
                    string3={formFields.subject[2]?.value ?? ""}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Stack>
          <Stack>
            <IconButton onClick={() => setManagingStep("")}>
              <SvgIcon>
                <CloseIcon />
              </SvgIcon>
            </IconButton>
          </Stack>
        </Stack>
        <Stack
          alignItems="center"
          justifyContent="flex-end"
          direction="row"
          spacing={1}
        >
          <Button
            size="small"
            variant="text"
            onClick={() => setManagingStep("")}
          >
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      </Stack>
    );
  };

  const stepCompletion = () => {
    const values = submitCampaign.abTestDetails?.testingValues;
    const expectedVariants = submitCampaign.abTestDetails?.testingVariants;
    return (
      Array.isArray(values) &&
      values.length === expectedVariants &&
      values.every((val) => val !== null && val !== undefined && val !== "")
    );
  };  

  const getNonExpandedView = () => {
    const onAnotherStep =
      managingStep !== "" && managingStep !== "subject-section";
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ opacity: onAnotherStep ? 0.5 : 1 }}
      >
        <Stack direction="row" spacing={4}>
          <SvgIcon>
            <CheckCircleIcon
              htmlColor={stepCompletion() ? "#7551D0" : "#C1C1C1"}
            />
          </SvgIcon>
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h4">Subject</Typography>
              <Chip label={"Testing Variable"} />
            </Stack>
            <Typography variant="body1">
              Following would be the subjects for different variants
            </Typography>
            {Array.from({
              length: submitCampaign.abTestDetails?.testingVariants ?? 0,
            }).map((_, i) => {
              return (
                <Typography variant="body2" color="text.secondary" pl={3}>
                  Version {i + 1}:{" "}
                  {submitCampaign.abTestDetails?.testingValues[i]}
                </Typography>
              );
            })}
          </Stack>
        </Stack>
        <Stack>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setManagingStep("subject-section");
            }}
            disabled={onAnotherStep}
          >
            {`${stepCompletion() ? "Edit" : "Add"}`} Subject
          </Button>
        </Stack>
      </Stack>
    );
  };

  const getPage = () => {
    if (managingStep === "subject-section") {
      return getExpandedView();
    }
    return getNonExpandedView();
  };

  return (
    <>
      <Box px={3}>{getPage()}</Box>
      <CustomLoader isLoading={saving} />
    </>
  );
};

export default SubjectSectionTestingVariable;
